import { DateTime, Interval } from "luxon";

export const getDurations = (start, end = 480) => {
  if (start === -1) {
    return [
      {
        value: -1,
        label: "All day"
      }
    ];
  }
  const step = 15;

  let hour = 0;
  let minutes = 0;
  let totalMinutes = start;
  let arr = [];

  while (totalMinutes <= end) {
    hour = Math.floor(totalMinutes / 60);
    minutes = totalMinutes - hour * 60;
    arr.push({
      value: totalMinutes,
      label: ("" + (hour ? hour + "h" : "") + (minutes ? " " + minutes + "m" : "")).trim()
    });
    totalMinutes += step;
  }
  return arr;
};

export const filterResourcesForNewReservation = (
  timezone,
  resourcesWithReservations,
  resources,
  date,
  selectedResource,
  duration,
  reservationFromForm
) => {
  const seletedTime = DateTime.fromJSDate(date).setZone(timezone);
  // const selectedResourceIdx = resourcesWithReservations.findIndex(r => r.id === selectedResource.id);
  const selectedDuration = duration === "-1" ? 0 : Number(duration);
  const selectedInterval = Interval.fromDateTimes(seletedTime, seletedTime.plus({ minutes: selectedDuration }));
  const newResourcesWithReservations = resourcesWithReservations
    .map(rwr => {
      return {
        ...rwr,
        children: rwr.children.map(child => {
          return child.map(r => {
            const reservTime = DateTime.fromJSDate(r.reservationTime.toDate()).setZone(timezone);
            const isAllDay = r.duration === -1;
            const reservEnd = reservTime.plus({ minutes: isAllDay ? 0 : r.duration });
            const interval = Interval.fromDateTimes(reservTime, reservEnd);
            const isSelectedDateInIntervalB = interval.contains(seletedTime);
            const isSelectedDateInInvervalA = selectedInterval.contains(reservTime);
            return {
              ...child,
              intersectionExist:
                reservTime.equals(seletedTime) || isSelectedDateInIntervalB || isSelectedDateInInvervalA || isAllDay
            };
          });
        })
      };
    })
    .filter(rwr => {
      const isEditing = rwr.id === reservationFromForm?.resourceId ?? false;
      const intersectionExists = rwr.children.reduce((prevExists, currChild) => {
        return prevExists + Number(currChild.some(r => r.intersectionExist));
      }, 0);
      // console.log(rwr.name, intersectionExists);
      return intersectionExists < rwr.children.length || (intersectionExists === rwr.children.length && isEditing);
    });
  return newResourcesWithReservations;
};

/**
 * Split one resource's appointments into as few parallel lanes as they need.
 *
 * Appointments get their own packer rather than reusing the restaurant one below, for
 * two reasons. The restaurant packer allocates lanes from `maxReservations`, which plan
 * §0 declares meaningless for appointment resources and which the resource modal stopped
 * writing in `602da76b` — so an appointment resource gets exactly one lane, and the
 * second of two overlapping bookings has nowhere to go and is dropped. That is the bug
 * where an overbooked resource shows two appointments in the list and one in the
 * timeline. And its "next booking must start in a later hour" rule is a table-turn
 * heuristic: applied to appointments it would split a stylist booked at 9:00, 9:30,
 * 10:00 and 10:30 across two lanes despite nothing overlapping.
 *
 * Plain greedy interval partitioning instead — first lane that is free takes it — which
 * yields exactly the maximum overlap depth, so a resource only grows a second row when
 * it is genuinely double-booked (§7 lets staff do that deliberately). Overlap is
 * half-open, matching the slot engine: an appointment ending at 7:30 does not collide
 * with one starting at 7:30 (§12.5).
 */
const packAppointmentsIntoLanes = (appointments, timezone) => {
  const startOf = appointment => DateTime.fromJSDate(appointment.reservationTime.toDate()).setZone(timezone);
  const lanes = [];

  [...appointments]
    .sort((a, b) => startOf(a).toMillis() - startOf(b).toMillis())
    .forEach(appointment => {
      const start = startOf(appointment);
      // -1 is the restaurant "all day" sentinel. Appointments always carry a real
      // duration, but a resource switched between the two flows could still hold one.
      const end =
        appointment.duration === -1 ? start.endOf("day") : start.plus({ minutes: Number(appointment.duration) || 0 });
      const lane = lanes.find(l => l.end.toMillis() <= start.toMillis());
      if (lane) {
        lane.items.push(appointment);
        lane.end = end;
      } else {
        lanes.push({ items: [appointment], end });
      }
    });

  // A resource with nothing booked still needs a row in the timeline.
  return lanes.length ? lanes.map(lane => lane.items) : [[]];
};

/**
 * How many appointments a resource may hold at once before the admin views call it
 * overbooked.
 *
 * `maxReservations` is honoured when a resource carries one, so a legacy resource
 * configured for two simultaneous bookings is not flagged for its second. Absent — which
 * is every resource the appointments modal has created since `602da76b` — it is 1, per
 * plan §0. Note the customer slot engine enforces 1 regardless of this field
 * (`isSlotFree` blocks on any busy interval), so anything above 1 can only ever come
 * from a staff booking (§7).
 */
export const simultaneousBookingCapacity = resource =>
  Number(resource?.maxReservations) > 0 ? Number(resource.maxReservations) : 1;

/**
 * Ids of the appointments sitting beyond their resource's capacity — the ones the admin
 * views tint red as a warning.
 *
 * Reads the packed lanes rather than re-deriving overlap: `packAppointmentsIntoLanes`
 * opens lane N only when the first N-1 are all busy at that instant, so anything in a
 * lane at or past the capacity index overlaps that many others by construction. It also
 * means the *later* booking is the one flagged, since the greedy packer fills lower
 * lanes with earlier starts.
 */
export const findOverbookedReservationIds = (resourcesWithReservations, isAppointments = false) => {
  const ids = new Set();
  if (!isAppointments || !resourcesWithReservations) {
    return ids;
  }
  resourcesWithReservations.forEach(resource => {
    (resource.children || []).slice(simultaneousBookingCapacity(resource)).forEach(lane => {
      lane.forEach(reservation => ids.add(reservation.id));
    });
  });
  return ids;
};

export const makeResourcesWithResertions = (resources, reservations, timezone, isAppointments = false) => {
  const rsrc = [...resources.map(r => ({ ...r, children: Array(r.maxReservations).fill([]) }))];

  const reservs = reservations
    .map(r => ({ id: r.id, ...r.data() }))
    .sort(
      (a, b) =>
        a.resourceId?.localeCompare(b.resourceId) ||
        DateTime.fromJSDate(a.reservationTime.toDate()).diff(DateTime.fromJSDate(b.reservationTime.toDate()))
    );

  if (isAppointments) {
    return rsrc.map(rsr => ({
      ...rsr,
      children: packAppointmentsIntoLanes(
        reservs.filter(r => r.resourceId === rsr.id),
        timezone
      )
    }));
  }

  return rsrc.map(rsr => ({
    ...rsr,
    children: rsr.children.map(() => {
      const children = [];
      let nearestReservationIdx = reservs.findIndex(r => {
        return r.resourceId === rsr.id;
      });
      while (nearestReservationIdx !== -1) {
        const prevReserv = reservs.splice(nearestReservationIdx, 1)[0];
        children.push(prevReserv);

        const isPreviousAllDay = prevReserv.duration === -1;
        const previousRStart = prevReserv.reservationTime.toDate();
        const previousRStartDateTime = DateTime.fromJSDate(previousRStart).setZone(timezone);
        const previousRInterval = Interval.fromDateTimes(
          previousRStartDateTime,
          previousRStartDateTime.plus({ minutes: isPreviousAllDay ? 0 : Number(prevReserv.duration) })
        );
        nearestReservationIdx = reservs.findIndex(r => {
          const rStartDT = DateTime.fromJSDate(r.reservationTime.toDate()).setZone(timezone);
          return (
            r.resourceId === rsr.id &&
            ((!previousRInterval.contains(rStartDT) &&
              rStartDT.startOf("hour") > previousRStartDateTime.startOf("hour") &&
              !isPreviousAllDay) ||
              (isPreviousAllDay && children.length === 0))
          );
        });
      }
      return children;
    })
  }));
};
